import { every, find } from "lodash";
import React, { FunctionComponent, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { AppStore } from "../../store";
import {
  Fetchable,
  FetchableError,
  FetchableSuccess,
  isFetchableError,
  isFetchableSuccess,
} from "../../store/common/fetchable/helpers";
import { LoaderActions } from "../../store/loader/actions";
import { LoaderState } from "../../store/loader/models";
import { Flex } from "../layout";
import { FileProgressLoader } from "./file-progress";
import { Spinner } from "./icon";

const Container = styled(Flex).attrs<
  { customHeight?: string },
  { customHeight?: string }
>((props) => ({ ...props, center: true }))`
  ${(props) => (props.customHeight ? `min-height: ${props.customHeight};` : "")}

  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  flex-direction: column;
`;

/**
 * A Loader that will fold the Fetchable. In this regard, Fetchable can be thought as the
 * Maybe monad. It will display an error panel if the Fetchable is an ErrorFetchable and if the Fetchable
 * is a success, then it will send the data to the children. By doing so, the children component will ALWAYS
 * receive full data.
 */

export type LoaderProps<T> = {
  data: Fetchable<T>[];
  children: (data: T[]) => JSX.Element | null;
  height?: string;
};

export function Loader<T>({ data, children, height }: LoaderProps<T>) {
  const dispatch = useDispatch();
  const loaderState = useSelector<AppStore, AppStore["loader"]>(
    (store) => store.loader
  );

  useEffect(() => {
    if (loaderState.state !== "loading") {
      if (data.find((d) => d.status === "loading")) {
        dispatch(LoaderActions.setDefaultLoader());
      } else {
        dispatch(LoaderActions.clearLoader());
      }
    } else {
      if (!data.find((d) => d.status === "loading")) {
        dispatch(LoaderActions.clearLoader());
      }
    }
    return () => {
      dispatch(LoaderActions.clearLoader());
    };
  }, [...data]);

  const isLoading = useMemo(() => data.find((d) => d.status === "loading"), [
    data,
  ]);

  // Get any data as Error or undefined
  const isError = useMemo<FetchableError | undefined>(
    () => find(data, isFetchableError),
    [data]
  );

  const { t } = useTranslation("common");

  // Get all the datas as Success or return undefined
  const isReady = useMemo<FetchableSuccess<T>[] | undefined>(() => {
    const allReady = every(data, isFetchableSuccess);
    return allReady ? (data as FetchableSuccess<T>[]) : undefined;
  }, [data]);

  if (isLoading) {
    return null; // Let the overlay error
  }
  if (isReady) {
    return children([...isReady.map((d) => d.data)]);
  }

  if (isError) {
    let errMsg;
    try {
      errMsg = JSON.parse(isError.err.message); // TODO Fix
    } catch (e) {
      errMsg = isError.err.message;
    }
    return (
      <Container customHeight={height}>
        <h2 className="h2">{t("error-has-occured")}</h2>
      </Container>
    );
  }
  return null;
}

/**
 *
 * Global Loader
 *
 * This loader is responsible for displaying the loading screen in the app. It can be used in conjunction with
 * useAppLoader.
 */

export const ProgressContainer = styled(Flex)`
  flex-grow: 1;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  z-index: 1;
  flex-direction: column;
  padding: 10vh 10vw;
  justify-content: flex-end;
  align-items: flex-end;
`;

export const GlobalLoader: FunctionComponent<{}> = () => {
  const loader = useSelector<AppStore, LoaderState>((store) => store.loader);
  const { t } = useTranslation("common");

  return loader.state !== "idle" ? (
    <ProgressContainer>
      {loader.state === "file" ? (
        <FileProgressLoader {...loader} />
      ) : (
        <Spinner />
      )}
      <p>{t("processing")}...</p>
    </ProgressContainer>
  ) : null;
};
